import React, { useContext, useEffect } from 'react';
import classes from './PostOnDate.module.scss';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { CalendarPicker } from '../../../../components/actions/CalendarPicker';
import { PostEditorValidationMessages } from '../../constants/validation-messages';
import {
  convertDateFormatSettingToString,
  convertScheduledDateFormToZonedDate
} from '../../../../helpers/timeFormatter';
import { TimePicker } from '../../../../components/actions/TimePicker';
import { TimePickerActions, TimePickerInputRules } from '../../../../components/actions/TimePicker/TimePicker.view';
import { SquareButtonIconsEnum } from '../../../../constants/square-button-icons';
import { SquareButton } from '../../../../components/actions/SquareButton';
import { useSelectedOrganization } from '../../../../hooks/useSelectedOrganization';
import { DateFormatSetting, TimezoneSetting } from '../../../../models/graphql';
import { ScheduledDateForm } from '../../../../models/post-editor';
import { PostEditorContext } from '../../states/PostEditorProvider';
import { PostEditorGroupDataContext } from '../../states/PostEditorGroupDataProvider';
import { TimezoneMap } from '../../../../constants/timezones';
import { useDidMount } from '../../../../hooks/useDidMount';
import { FormElementAction } from '../../../../models/basic-objects';
import { useSettings } from 'hooks/useSettings';
import { ActiveCustomizeIndexContext } from '../../states/ActiveCustomizeIndexProvider';
import { Tooltip } from '../../../../components/actions/Tooltip';
import classNames from 'classnames';
import inputClasses from '../../../../components/forms/form.module.scss';
import { ErrorMessageSeverity } from '../../../../constants/form';
import { InputErrorMessageSeverityType } from '../../../../components/forms/Select/Select.view';
import scssVars from '../../../../assets/scss/exportedVars.module.scss';

export type OnUpdatePostOnTimeFunction = (date: Date, complete?: (keepNew?: boolean) => void) => void;

export type PostOnDateViewProps = {
  //
  action?: VoidFunction;
  onUpdate: OnUpdatePostOnTimeFunction;
  date?: Date;
  disabled?: boolean;
  formInstance: UseFormReturn;
  isPast?: boolean;
  label?: string;
  name: string;
  time?: Date;
  wrapper?: boolean;
  error?: string;
  warning?: string;
};

export const PostOnDateView = ({
  action,
  date,
  disabled,
  formInstance,
  isPast,
  label,
  name,
  onUpdate,
  time,
  wrapper,
  error,
  warning
}: PostOnDateViewProps): JSX.Element => {
  const { selectedOrgSettings } = useSettings();
  const { selectedOrganization, selectedWorkspace } = useSelectedOrganization();
  const rawDate = selectedOrganization?.myUserAssignment?.settings.dateFormat ?? DateFormatSetting.DAY_MONTH_YEAR;
  const dateFormat = convertDateFormatSettingToString(rawDate);
  const { state } = useContext(PostEditorContext);
  const [tabsState] = useContext(PostEditorGroupDataContext);
  const { postGroupIndex } = tabsState;
  const customizeIndex = useContext(ActiveCustomizeIndexContext);
  const { postGroups } = state;
  const postGroup = postGroups[postGroupIndex];
  const errorClasses = classNames(inputClasses.HelpTextErrorClass, inputClasses.HelpText);
  const {
    groupPostAttributes: {
      scheduling: { scheduledTimezone }
    },
    posts
  } = postGroup;
  const timezoneState =
    typeof customizeIndex === 'number' ? posts[customizeIndex].scheduling.scheduledTimezone : scheduledTimezone;
  const timezone = timezoneState ?? selectedWorkspace?.timezone ?? TimezoneSetting.UTC;

  const minDate = new Date();

  const dateRules: RegisterOptions = {
    required: PostEditorValidationMessages.schedule.postOn.date.required
  };
  const timeRules: TimePickerInputRules = {
    hours: {
      required: PostEditorValidationMessages.schedule.postOn.hours.required
    },
    minutes: { required: PostEditorValidationMessages.schedule.postOn.minutes.required },
    ampm: { required: PostEditorValidationMessages.schedule.postOn.ampm.required }
  };
  const updateValue = (
    type: keyof TimePickerActions,
    value: string | Date,
    complete: (keepNewValue?: boolean) => void
  ) => {
    const formData: ScheduledDateForm = formInstance.getValues(name);
    // we need to do check here since am | pm are not of type string
    if (type === 'ampm') {
      formData.ampm = value === 'am' || value === 'pm' ? value : undefined;
    } else if (typeof value === 'string') {
      formData[type] = value;
    }
    const date = convertScheduledDateFormToZonedDate(formData, TimezoneMap[timezone]);
    onUpdate(date, complete);
  };

  const timePickerActions: TimePickerActions = {
    hour: (value, complete) => (typeof value === 'string' ? updateValue('hour', value, complete) : undefined),
    minute: (value, complete) => (typeof value === 'string' ? updateValue('minute', value, complete) : undefined),
    ampm: (value, complete) => (typeof value === 'string' ? updateValue('ampm', value, complete) : undefined)
  };

  const calendarUpdateAction: FormElementAction<Date> = (date, complete) => {
    const formData: ScheduledDateForm = formInstance.getValues(name);
    const midnight = date.setHours(0, 0, 0, 0);
    formData.date = new Date(midnight);
    const newDate = convertScheduledDateFormToZonedDate(formData, TimezoneMap[timezone]);
    onUpdate(newDate, complete);
  };
  useEffect(() => {
    formInstance.trigger().catch(() => {
      // prevent run time error
    });
  }, [formInstance]);

  useDidMount(
    () => () => formInstance.unregister(name, { keepValue: false, keepDefaultValue: false, keepError: false })
  );

  let formElementsError: InputErrorMessageSeverityType = {
    errorMessage: undefined
  };
  if (error) {
    formElementsError = {
      errorMessage: '',
      errorSeverity: ErrorMessageSeverity.error
    };
  } else if (warning) {
    formElementsError = {
      errorMessage: '',
      errorSeverity: ErrorMessageSeverity.warning
    };
  }

  const element: JSX.Element = (
    <>
      <div className={'grid-x grid-padding-x'}>
        <div className={'cell small-12 margin-top-small-16'}>
          <CalendarPicker
            isError={error}
            isWarning={warning}
            updateAction={calendarUpdateAction}
            name={`${name}.date`}
            useFormInstance={formInstance}
            inputSize={'large'}
            rules={dateRules}
            placeholder={'Select Date'}
            {...(date ? { date } : {})}
            {...(disabled ? { disabled } : {})}
            {...(label ? { label } : {})}
            calendarProps={{
              dateFormat,
              minDate
            }}
            startDay={selectedOrgSettings?.startOfWeekFormat}
          />
        </div>
        <div className={'cell small-12 margin-top-small-8'}>
          <TimePicker
            actions={timePickerActions}
            {...formElementsError}
            {...(disabled ? { disabled } : {})}
            {...(time ? { defaultDate: time } : {})}
            useFormInstance={formInstance}
            hoursInputName={`${name}.hour`}
            minutesInputName={`${name}.minute`}
            ampmInputName={`${name}.ampm`}
            size={'large'}
            rules={timeRules}
          />
        </div>
        {error && !isPast && (
          <div className={'cell small-12 margin-top-small-4'}>
            <p className={errorClasses}>{error}</p>
          </div>
        )}
        {warning && !isPast && (
          <div className={'cell small-12 margin-top-small-4'}>
            <p className={errorClasses}>{warning}</p>
          </div>
        )}
        {isPast && (
          <div
            className={classNames(classes.IsPastTooltipWrapper, { [classes.IsPastTooltipWrapperWithLabel]: !!label })}>
            <Tooltip
              target={`#tooltipIsPastTarget_${
                customizeIndex !== undefined ? customizeIndex : 'group'
              }_${postGroupIndex}`}
              position={'left'}
              pointer
              type={'inlined'}
              autoZIndex={false}
              style={{ zIndex: scssVars['z-index-overmodal'] }}>
              <>This post has already been published. Past dates are displayed for reference and cannot be changed.</>
            </Tooltip>
          </div>
        )}
        {action && (
          <div className={classes.CloseIcon}>
            <SquareButton type={'transparent'} icon={SquareButtonIconsEnum.closeSmall} action={action} />
          </div>
        )}
      </div>
    </>
  );
  return wrapper ? <div className={classes.PostOnWrapper}>{element}</div> : element;
};
